import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Menu, X } from 'lucide-react';
import { UdvegaLogo } from '../components/UdvegaLogo';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Solutions', path: '/solutions' },
  { label: 'Success Stories', path: '/success-stories' },
  { label: 'About', path: '/about' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-slate-50/80 border-b border-slate-200/60" data-testid="navbar">
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
          <UdvegaLogo size="sm" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`text-sm font-medium transition-colors ${
                location.pathname === link.path
                  ? 'text-teal-600'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {user && user.email ? (
            <>
              <Link to="/dashboard">
                <Button variant="outline" size="sm" className="border-teal-600 text-teal-600 hover:bg-teal-50" data-testid="nav-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
              <Button size="sm" onClick={logout} variant="ghost" className="text-slate-500" data-testid="nav-logout-btn">
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-slate-600" data-testid="nav-login-btn">
                  Log in
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="nav-demo-btn">
                  Book a Demo
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
        </button>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200"
          >
            <div className="px-6 py-4 flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={`text-sm font-medium py-2 ${
                    location.pathname === link.path ? 'text-teal-600' : 'text-slate-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user && user.email ? (
                <Button size="sm" onClick={() => { logout(); setMobileOpen(false); }} className="mt-2 bg-teal-600 text-white">
                  Logout
                </Button>
              ) : (
                <Link to="/login" onClick={() => setMobileOpen(false)}>
                  <Button size="sm" className="mt-2 bg-teal-600 text-white w-full">Log in</Button>
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
